import type { AgendaModule } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import { Formik } from "formik";
import * as yup from "yup";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { TagSelection } from "../tags";
import { AgendaModuleSortableList } from "../dnd";
import { PreviewAgendaTemplateDialog } from "./PreviewAgendaTemplateDialog";
import { useRef, useState } from "react";
import { SearchableAgendaModuleTable } from "../common/SearchableTables";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { useConfirmNavigate } from "@/hooks/useConfirmNavigate";
import { deepCompare } from "@repo/lib/deep";
import { useAgendaModuleList } from "@/hooks/queryHooks/agendaModuleHooks";
import { useActiveTeamId } from "@/hooks/useActiveTeam";
import { useLetterHeadList } from "@/hooks/queryHooks/letterHeadHooks";
import { useTeamList } from "@/hooks/queryHooks/teamHooks";
import { agendaRepo, type AgendaDto } from "@/repositories/agendaRepo";
import type { AgendaModuleDto } from "@/repositories/agendaModuleRepo";
import { meetingRepo } from "@/repositories/meetingRepo";

interface AgendaTemplateFormProps {
  tenantId: string;
  agendaTemplateFormData?: AgendaDto;
  onCancel?: () => void;
  onSave?: () => void;
}
export const AgendaTemplateForm = ({
  tenantId,
  agendaTemplateFormData,
  onCancel,
  onSave,
}: AgendaTemplateFormProps) => {
  useBlockUnload();

  const activeTeamId = useActiveTeamId() || "";

  const { data: letterheads } = useLetterHeadList(tenantId);
  const { data: teams } = useTeamList(tenantId);
  const { data: agendaModules } = useAgendaModuleList(tenantId, activeTeamId);

  const initialValues = useRef<AgendaDto>(
    agendaTemplateFormData
      ? {
          teamId: activeTeamId,
          ...agendaTemplateFormData,
          letterHeadId:
            agendaTemplateFormData.letterHeadId === null
              ? "null"
              : agendaTemplateFormData.letterHeadId === undefined
                ? ""
                : agendaTemplateFormData.letterHeadId,
          template: true,
        }
      : agendaRepo.init({ teamId: activeTeamId, template: true })
  );
  const latestValues = useRef<AgendaDto>(initialValues.current);

  // if team didn't load immediately, then
  // make sure initialValues / latestValues don't carry empty teamId:
  initialValues.current.teamId ||= activeTeamId;
  latestValues.current.teamId ||= activeTeamId;

  const confirmNavigate = useConfirmNavigate(() => {
    if (deepCompare(initialValues.current, latestValues.current)) return false;
    return true;
  });

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const getAgendaModules = (ids: string[]) => {
    if (!agendaModules) return [];

    const responseVals: (AgendaModuleDto & { isEditable: boolean })[] = [];

    for (const id of ids) {
      const agendaModule = agendaModules.find(
        (agendaModule) => agendaModule.id === id
      );

      if (agendaModule) {
        responseVals.push({
          ...agendaModule,
          isEditable: true, // !agendaModule.template ?? false,
        });
      }
    }
    return responseVals;
  };

  const validationSchema = yup.object({
    name: yup.string().required("Agenda name is required"),
    letterHeadId: yup
      .string()
      .notOneOf([""])
      .required("Letterhead is required"),
    teamId: yup.string().notOneOf([""]).required("Group is required"),
  });

  if (!activeTeamId) return null;

  return (
    <div className="max-w-screen-lg w-full mx-auto mt-2">
      {confirmNavigate}
      <Formik
        initialValues={initialValues.current}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          if (!tenantId) {
            console.log("No tenant ID", tenantId);
            return;
          }

          if (values.letterHeadId === "null") {
            values = { ...values, letterHeadId: null };
          }

          // create or update agenda template
          if (agendaTemplateFormData) {
            await agendaRepo.update(tenantId, values.id, { ...values });
          } else {
            await agendaRepo.create(tenantId, { ...values });
          }

          initialValues.current = latestValues.current;

          if (onSave) onSave();
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          resetForm,
          errors,
          isValid,
          touched,
          isSubmitting,
        }) => {
          latestValues.current = values;

          const onAddAgendaModule = (item: AgendaModule) => {
            setFieldValue(
              "agendaModuleIds",
              values.agendaModuleIds
                ? [...values.agendaModuleIds, item.id]
                : [item.id]
            );
            setFieldValue(
              "agendaModules",
              values.agendaModules ? [...values.agendaModules, item] : [item]
            );
          };

          return (
            <form onSubmit={handleSubmit}>
              <PreviewAgendaTemplateDialog
                open={isPreviewOpen}
                setOpen={setIsPreviewOpen}
                meeting={meetingRepo.init({
                  name: "{Meeting Title}",
                  agenda: values,
                })}
                letterHead={values.letterHead ?? undefined}
              />
              <div className="flex flex-col justify-start gap-y-1">
                <div className="flex flex-row justify-between gap-x-6 min-h-[180px]">
                  <div className="flex flex-col w-1/2 gap-y-1">
                    <Input
                      id="name"
                      label="*Template Title"
                      name="name"
                      disabled={isSubmitting}
                      orientation="horizontal"
                      value={values.name ?? ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.name && touched.name}
                      errorMessage={errors.name}
                      labelStyle="w-[256px] text-right"
                    />
                    <SelectInput
                      label="*Letterhead"
                      disabled={isSubmitting}
                      placeholder="Select a letterhead"
                      value={values.letterHeadId ?? ""}
                      onValueChange={(value) => {
                        setFieldValue(
                          "letterHead",
                          letterheads?.find((lh) => lh.id === value)
                        );
                        setFieldValue("letterHeadId", value);
                      }}
                      options={[
                        <SelectItem key="null" value="null">
                          No Letterhead
                        </SelectItem>,
                        ...(letterheads?.map((lh) => (
                          <SelectItem key={lh.id} value={lh.id}>
                            {lh.viewName} {lh.name ? `(${lh.name})` : null}
                          </SelectItem>
                        )) ?? []),
                      ]}
                      orientation="horizontal"
                      labelStyle="w-[256px] text-right"
                      // we can't wait for 'touched', because it doesn't happen until field is valid
                      error={!!errors.letterHeadId}
                      errorMessage={errors.letterHeadId}
                    />
                    <SelectInput
                      label="Team"
                      disabled
                      value={values.teamId ?? ""}
                      onValueChange={(value) => setFieldValue("teamId", value)}
                      options={
                        teams
                          ? teams.map((lh) => (
                              <SelectItem key={lh.id} value={lh.id}>
                                {lh.name}
                              </SelectItem>
                            ))
                          : []
                      }
                      orientation="horizontal"
                      labelStyle="w-[256px] text-right"
                    />
                  </div>
                  <div className="flex flex-col w-1/2 gap-y-3">
                    <Button
                      className="mt-1.5"
                      disabled={isSubmitting}
                      onClick={() => setIsPreviewOpen(true)}
                    >
                      Preview
                    </Button>
                    <TagSelection
                      label="Tags"
                      disabled={isSubmitting}
                      value={values.tags ?? []}
                      onChange={(newTags) => setFieldValue("tags", newTags)}
                      enableAutocomplete={true}
                      useStandardizedTags={true}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-y-1">
                  {agendaModules && (
                    <AgendaModuleSortableList
                      disabled={isSubmitting}
                      items={getAgendaModules(values.agendaModuleIds)}
                      onChange={(items) => {
                        setFieldValue(
                          "agendaModuleIds",
                          items.map((item) => item.id)
                        );
                        setFieldValue("agendaModules", items);
                      }}
                      editableLabel="Edit Agenda Section"
                      notEditableLabel="Edit the Agenda Section template from the settings page"
                      deleteLabel="Remove Agenda Section"
                    />
                  )}
                  {agendaModules && (
                    <SearchableAgendaModuleTable
                      disabled={isSubmitting}
                      data={agendaModules?.filter(
                        (doc) =>
                          !values.agendaModuleIds?.includes(doc.id) &&
                          doc.template
                      )}
                      onSubmit={onAddAgendaModule}
                      onAdd={onAddAgendaModule}
                      isTemplate
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-row justify-between mt-10">
                <Button
                  variant="destructive"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (onCancel) onCancel();
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  disabled={!isValid || isSubmitting}
                  type="submit"
                >
                  Save
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
